<template>
  <div class="admin-shell">
    <aside class="admin-shell__sidebar">
      <div class="admin-shell__top">
      <div class="admin-shell__brand-row">
        <div class="admin-shell__brand">
          <span class="admin-shell__brand-mark">S</span>
          <div>
            <p class="admin-shell__brand-title">Sauda</p>
            <p class="admin-shell__brand-sub">{{ $t('nav.admin') }}</p>
          </div>
        </div>
        <button type="button" class="admin-shell__mobile-cash" @click="$router.push('/cash')">
          <i class="mdi mdi-point-of-sale"></i>
          <span>{{ $t('nav.openCash') }}</span>
        </button>
      </div>

      <div class="admin-shell__tools">
        <LanguageSwitcher dark />
        <label
          class="admin-shell__touch"
          :class="{ 'is-on': touchMode }"
          :title="touchMode ? $t('common.touchScreenOn') : $t('common.touchScreenOff')"
        >
          <input
            type="checkbox"
            :checked="touchMode"
            @change="toggleTouchMode"
          >
          <span>{{ $t('common.touchScreen') }}</span>
        </label>
        <button type="button" class="admin-shell__cash" @click="$router.push('/cash')">
          <i class="mdi mdi-point-of-sale"></i>
          <span>{{ $t('nav.openCash') }}</span>
        </button>
      </div>
      </div>

      <nav class="admin-shell__nav">
        <button
          v-for="item in menu"
          :key="item.to"
          type="button"
          class="admin-shell__nav-item"
          :class="{ 'admin-shell__nav-item--active': isActive(item.to) }"
          @click="$router.push(item.to)"
        >
          <i :class="['mdi', item.icon]"></i>
          <span>{{ $t(item.label) }}</span>
        </button>
      </nav>
    </aside>

    <main class="admin-shell__content">
      <router-view />
    </main>

    <TouchNumberHost />
  </div>
</template>

<script>
import LanguageSwitcher from '@/components/LanguageSwitcher'
import TouchNumberHost from '@/components/TouchNumberHost'
import { isTouchModeEnabled, setTouchModeEnabled } from '@/utils/touchMode'

const CASHIER_PATHS = ['/admin_panel', '/category_control', '/supplier_control', '/stock']

export default {
  name: 'AdminControl',
  components: {
    LanguageSwitcher,
    TouchNumberHost,
  },
  data() {
    return {
      touchMode: isTouchModeEnabled(),
      allMenu: [
        { label: 'nav.categories', to: '/category_control', icon: 'mdi-folder-outline' },
        { label: 'nav.suppliers', to: '/supplier_control', icon: 'mdi-truck-outline' },
        { label: 'nav.products', to: '/admin_panel', icon: 'mdi-package-variant-closed' },
        { label: 'nav.barcodes', to: '/barcodes', icon: 'mdi-barcode' },
        { label: 'nav.priceTags', to: '/price-tags', icon: 'mdi-tag-text-outline' },
        { label: 'nav.catalogMenu', to: '/catalog_menu', icon: 'mdi-file-swap-outline' },
        { label: 'nav.saleMode', to: '/sale_mode', icon: 'mdi-tune-variant' },
        { label: 'nav.paymentTypes', to: '/payment_types', icon: 'mdi-credit-card-outline' },
        { label: 'nav.discounts', to: '/discounts', icon: 'mdi-sale-outline' },
        { label: 'nav.modifiers', to: '/modifiers', icon: 'mdi-plus-box-multiple-outline' },
        { label: 'nav.cashiers', to: '/cashiers', icon: 'mdi-account-group-outline' },
        { label: 'nav.bonuses', to: '/bonus_clients', icon: 'mdi-gift-outline' },
        { label: 'nav.stock', to: '/stock', icon: 'mdi-warehouse' },
        { label: 'nav.sales', to: '/sales', icon: 'mdi-cash-register' },
        { label: 'nav.loans', to: '/loans', icon: 'mdi-hand-coin-outline' },
        { label: 'nav.analytics', to: '/analytic', icon: 'mdi-chart-line' },
      ],
    }
  },
  computed: {
    isCashier() {
      return localStorage.getItem('company_role') === 'cashier'
    },
    canManageProducts() {
      return localStorage.getItem('can_manage_products') === '1'
    },
    menu() {
      if (!this.isCashier) return this.allMenu
      return this.allMenu.filter((item) => CASHIER_PATHS.includes(item.to))
    },
  },
  mounted() {
    if (!localStorage.getItem('access_token')) {
      this.$router.push('/')
      return
    }
    if (this.isCashier && !this.canManageProducts) {
      this.$router.push('/cash')
      return
    }
    this.guardRoute(this.$route.path)
  },
  watch: {
    '$route.path'(path) {
      this.guardRoute(path)
    },
  },
  methods: {
    isActive(path) {
      return this.$route.path === path
    },
    guardRoute(path) {
      if (this.isCashier && !CASHIER_PATHS.includes(path)) {
        this.$router.push('/admin_panel')
      }
    },
    toggleTouchMode() {
      this.touchMode = setTouchModeEnabled(!this.touchMode)
    },
  },
}
</script>

<style scoped lang="scss">
.admin-shell {
  --admin-ink: #152033;
  --admin-muted: #6b7a90;
  --admin-accent: #0b4f8a;
  --admin-accent-soft: #e8f1f8;
  --admin-surface: #f3f6fa;
  --admin-sidebar: #10243a;

  display: flex;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  min-height: 100dvh;
  background: var(--admin-surface);
  color: var(--admin-ink);
}

.admin-shell__sidebar {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  padding-top: calc(24px + env(safe-area-inset-top, 0px));
  background:
    radial-gradient(circle at top left, rgba(46, 140, 210, 0.22), transparent 42%),
    linear-gradient(180deg, #143252 0%, var(--admin-sidebar) 100%);
  color: #fff;
}

.admin-shell__top,
.admin-shell__brand-row {
  display: contents;
}

.admin-shell__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 8px 28px;
}

.admin-shell__brand-mark {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #fff;
  color: var(--admin-accent);
  font-weight: 800;
  font-size: 20px;
}

.admin-shell__brand-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.admin-shell__brand-sub {
  margin: 2px 0 0;
  font-size: 12px;
  opacity: 0.7;
}

.admin-shell__nav {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  margin-top: 16px;
  overflow-y: auto;
}

.admin-shell__nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: rgba(255, 255, 255, 0.82);
  font-size: 15px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

  .mdi {
    font-size: 20px;
    opacity: 0.9;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }

  &--active {
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    box-shadow: inset 3px 0 0 #7ec2ff;
  }
}

.admin-shell__tools {
  display: grid;
  gap: 10px;
}

.admin-shell__touch {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.88);
  font-weight: 650;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  user-select: none;

  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    margin: 0;
    accent-color: #38bdf8;
    cursor: pointer;
    flex-shrink: 0;
  }

  &.is-on {
    background: rgba(56, 189, 248, 0.22);
    border-color: rgba(125, 211, 252, 0.55);
    color: #e0f2fe;
  }
}

.admin-shell__cash {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 4px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
}

.admin-shell__mobile-cash {
  display: none;
}

.admin-shell__content {
  flex: 1;
  min-width: 0;
  overflow: auto;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  -webkit-overflow-scrolling: touch;
}

/* Планшет: узкий сайдбар — только иконки */
@media (max-width: 1100px) and (min-width: 901px) {
  .admin-shell__sidebar {
    width: 76px;
    padding: 20px 10px;
    padding-top: calc(20px + env(safe-area-inset-top, 0px));
    padding-left: calc(10px + env(safe-area-inset-left, 0px));
  }

  .admin-shell__brand {
    justify-content: center;
    padding: 4px 0 24px;

    > div {
      display: none;
    }
  }

  .admin-shell__nav-item {
    justify-content: center;
    padding: 12px;

    span {
      display: none;
    }

    .mdi {
      font-size: 22px;
    }
  }

  .admin-shell__cash,
  .admin-shell__touch {
    padding: 12px;

    span {
      display: none;
    }
  }

  .admin-shell__tools {
    justify-items: center;
  }
}

/* Телефон: верхняя панель с горизонтальным меню */
@media (max-width: 900px) {
  .admin-shell {
    flex-direction: column;
  }

  .admin-shell__sidebar {
    position: sticky;
    top: 0;
    z-index: 15;
    width: 100%;
    min-height: auto;
    padding: 12px 12px 8px;
    padding-top: calc(12px + env(safe-area-inset-top, 0px));
    padding-left: calc(12px + env(safe-area-inset-left, 0px));
    padding-right: calc(12px + env(safe-area-inset-right, 0px));
  }

  .admin-shell__top {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin-bottom: 8px;
  }

  .admin-shell__brand-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .admin-shell__brand {
    min-width: 0;
    padding: 0;
  }

  .admin-shell__brand-mark {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    font-size: 16px;
  }

  .admin-shell__brand-title {
    font-size: 15px;
  }

  .admin-shell__brand-sub {
    display: none;
  }

  .admin-shell__nav {
    flex-direction: row;
    overflow-x: auto;
    flex: initial;
    gap: 6px;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .admin-shell__nav-item {
    white-space: nowrap;
    padding: 9px 12px;
    font-size: 13px;

    .mdi {
      font-size: 17px;
    }

    &--active {
      box-shadow: inset 0 -2px 0 #7ec2ff;
    }
  }

  .admin-shell__cash {
    display: none;
  }

  .admin-shell__tools {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: 8px;
    position: static;
    width: 100%;
  }

  .admin-shell__tools ::v-deep .language-switcher {
    display: inline-flex;
    align-items: center;
    height: 36px;
    box-sizing: border-box;
  }

  .admin-shell__touch {
    height: 36px;
    justify-content: flex-start;
    min-width: 0;
    padding: 0 10px;
    font-size: 12px;
    box-sizing: border-box;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .admin-shell__mobile-cash {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    height: 36px;
    margin: 0;
    gap: 5px;
    padding: 0 10px;
    border: 1px solid rgba(255, 255, 255, .24);
    border-radius: 9px;
    background: rgba(255, 255, 255, .1);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;

    .mdi {
      font-size: 16px;
    }
  }
}
</style>
